import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import {
  CalendarDays, Plus, Clock, CheckCircle2, XCircle, ChevronLeft, ChevronRight,
  Inbox, FileText, Hourglass, Ban
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API = '/api';

const STATUS_MAP = {
  pending: { label: 'Čeká', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock, dot: '#F59E0B' },
  approved: { label: 'Schváleno', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle2, dot: '#16A34A' },
  rejected: { label: 'Zamítnuto', color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle, dot: '#DC2626' },
};

const DAYS_CZ = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];
const MONTHS_CZ = ['Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen', 'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'];

function getMonthDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  let startOffset = firstDay.getDay() - 1;
  if (startOffset < 0) startOffset = 6;
  const days = [];
  for (let i = 0; i < startOffset; i++) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);
  return days;
}

// Build local YYYY-MM-DD (no timezone shift!)
function toIsoDate(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// Compare YYYY-MM-DD strings (lexicographic = chronological)
function isInRange(year, month, day, from, to) {
  if (!day || !from || !to) return false;
  const d = toIsoDate(year, month, day);
  const f = String(from).slice(0, 10);
  const t = String(to).slice(0, 10);
  return d >= f && d <= t;
}

function fmtCz(d) {
  if (!d) return '—';
  const s = String(d).slice(0, 10);
  const p = s.split('-');
  if (p.length !== 3) return s;
  return `${p[2]}.${p[1]}.${p[0]}`;
}

function todayLocalIso() {
  const n = new Date();
  return toIsoDate(n.getFullYear(), n.getMonth(), n.getDate());
}

// ===== Compact list item =====
function RequestItem({ r, showUser = false, dense = true }) {
  const st = STATUS_MAP[r.status] || STATUS_MAP.pending;
  const StIcon = st.icon;
  return (
    <div
      className={`flex items-center justify-between gap-2 rounded-lg border border-[#F1F5F9] hover:bg-[#F8FAF8] transition-colors ${dense ? 'p-2' : 'p-3'}`}
      data-testid={`req-${r.id}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-1 h-9 rounded-full shrink-0" style={{ backgroundColor: r.leave_type_color }} />
        <div className="min-w-0">
          <p className="text-sm font-medium text-[#1C1917] truncate">
            {showUser ? r.user_name : r.leave_type_name}
          </p>
          <p className="text-[11px] text-[#57534E]">
            {showUser && <span className="text-[#94A3B8]">{r.leave_type_name} · </span>}
            {fmtCz(r.date_from)} — {fmtCz(r.date_to)}
            {r.department_name && showUser && <span className="text-[#94A3B8]"> · {r.department_name}</span>}
          </p>
        </div>
      </div>
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border shrink-0 ${st.color}`}
        title={r.decision_note || ''}
      >
        <StIcon className="w-3 h-3" /> {st.label}
      </span>
    </div>
  );
}

export default function VacationPage() {
  const [requests, setRequests] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [pendingForMe, setPendingForMe] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [allFilter, setAllFilter] = useState('pending');

  // Reject dialog
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [decidingId, setDecidingId] = useState(null);

  // Calendar
  const now = new Date();
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());

  // Form
  const [formFrom, setFormFrom] = useState('');
  const [formTo, setFormTo] = useState('');
  const [formType, setFormType] = useState('');
  const [formNote, setFormNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [reqRes, myRes, typesRes, pendingRes] = await Promise.all([
        axios.get(`${API}/vacation/requests`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/vacation/my-requests`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/leave-types`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/vacation/pending-for-me`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setRequests(reqRes.data || []);
      setMyRequests(myRes.data || []);
      setLeaveTypes(typesRes.data || []);
      setPendingForMe(pendingRes.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async () => {
    if (!formFrom || !formTo || !formType) {
      toast.error('Vyplňte všechna povinná pole');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/vacation/request`, {
        date_from: formFrom,
        date_to: formTo,
        leave_type_id: formType,
        note: formNote,
      }, { withCredentials: true });
      toast.success('Žádost odeslána');
      setShowDialog(false);
      setFormFrom(''); setFormTo(''); setFormType(''); setFormNote('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba při odesílání');
    } finally {
      setSubmitting(false);
    }
  };

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); }
    else setCalMonth(calMonth - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); }
    else setCalMonth(calMonth + 1);
  };

  const handleApprove = async (req) => {
    setDecidingId(req.id);
    try {
      await axios.post(`${API}/vacation/decide-inapp/${req.id}`, { action: 'approve' }, { withCredentials: true });
      toast.success(`Žádost ${req.user_name} schválena`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba při schvalování');
    } finally {
      setDecidingId(null);
    }
  };

  const openReject = (req) => {
    setRejectTarget(req);
    setRejectReason('');
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setDecidingId(rejectTarget.id);
    try {
      await axios.post(`${API}/vacation/decide-inapp/${rejectTarget.id}`, { action: 'reject', reason: rejectReason }, { withCredentials: true });
      toast.success(`Žádost ${rejectTarget.user_name} zamítnuta`);
      setRejectTarget(null);
      setRejectReason('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba při zamítání');
    } finally {
      setDecidingId(null);
    }
  };

  // KPI counts from MY requests
  const myCounts = useMemo(() => {
    const c = { total: myRequests.length, pending: 0, approved: 0, rejected: 0 };
    myRequests.forEach(r => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [myRequests]);

  // Filtered "all" list
  const filteredAll = useMemo(() => {
    if (allFilter === 'all') return requests;
    return requests.filter(r => r.status === allFilter);
  }, [requests, allFilter]);

  // Calendar requests (only approved + pending; rejected hidden)
  const days = getMonthDays(calYear, calMonth);
  const todayStr = todayLocalIso();
  const monthStart = toIsoDate(calYear, calMonth, 1);
  const monthEnd = toIsoDate(calYear, calMonth, new Date(calYear, calMonth + 1, 0).getDate());
  const monthRequests = requests.filter(r => {
    if (r.status === 'rejected') return false;
    const f = String(r.date_from).slice(0, 10);
    const t = String(r.date_to).slice(0, 10);
    return f <= monthEnd && t >= monthStart;
  });

  if (loading) {
    return <Layout><div className="p-12 text-center text-[#57534E]">Načítám...</div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-4" data-testid="vacation-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#052E16] font-['Manrope'] flex items-center gap-2">
              <CalendarDays className="w-7 h-7 text-[#16A34A]" /> Dovolená
            </h1>
            <p className="text-xs text-[#57534E]">Přehled a žádosti o dovolenou</p>
          </div>
          <Button
            onClick={() => setShowDialog(true)}
            data-testid="new-vacation-btn"
            className="bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" /> Nová žádost
          </Button>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="p-3 rounded-xl border border-[#E2E8F0] bg-white shadow-sm" data-testid="kpi-total">
            <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold text-[#94A3B8]"><FileText className="w-3 h-3" /> Moje celkem</div>
            <div className="text-2xl font-bold text-[#052E16] tabular-nums">{myCounts.total}</div>
          </div>
          <div className="p-3 rounded-xl border border-amber-200 bg-amber-50/40 shadow-sm" data-testid="kpi-pending">
            <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold text-amber-700"><Hourglass className="w-3 h-3" /> Čeká</div>
            <div className="text-2xl font-bold text-[#052E16] tabular-nums">{myCounts.pending || 0}</div>
          </div>
          <div className="p-3 rounded-xl border border-green-200 bg-green-50/40 shadow-sm" data-testid="kpi-approved">
            <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold text-green-700"><CheckCircle2 className="w-3 h-3" /> Schváleno</div>
            <div className="text-2xl font-bold text-[#052E16] tabular-nums">{myCounts.approved || 0}</div>
          </div>
          <div className="p-3 rounded-xl border border-red-200 bg-red-50/40 shadow-sm" data-testid="kpi-rejected">
            <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold text-red-700"><Ban className="w-3 h-3" /> Zamítnuto</div>
            <div className="text-2xl font-bold text-[#052E16] tabular-nums">{myCounts.rejected || 0}</div>
          </div>
        </div>

        {/* Pending for me (supervisor) */}
        {pendingForMe.length > 0 && (
          <Card className="bg-white border-[#E2E8F0] shadow-sm border-l-4 border-l-amber-500" data-testid="pending-for-me-card">
            <CardHeader className="pb-2 pt-3">
              <CardTitle className="text-sm font-bold text-[#052E16] font-['Manrope'] flex items-center gap-2">
                <Inbox className="w-4 h-4 text-amber-600" />
                Ke schválení <span className="text-amber-700">({pendingForMe.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 pb-3">
              <div className="space-y-1.5">
                {pendingForMe.map(r => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between gap-2 p-2 rounded-lg border border-amber-200 bg-amber-50/30"
                    data-testid={`approval-row-${r.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div className="w-1 h-9 rounded-full shrink-0" style={{ backgroundColor: r.leave_type_color }} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[#1C1917] truncate">{r.user_name}</p>
                        <p className="text-[11px] text-[#57534E]">{r.leave_type_name} · {fmtCz(r.date_from)} — {fmtCz(r.date_to)}</p>
                        {r.note && <p className="text-[10px] text-[#94A3B8] italic truncate" title={r.note}>„{r.note}"</p>}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="sm"
                        onClick={() => handleApprove(r)}
                        disabled={decidingId === r.id}
                        data-testid={`approve-btn-${r.id}`}
                        className="bg-[#16A34A] hover:bg-[#15803D] text-white h-7 px-2"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openReject(r)}
                        disabled={decidingId === r.id}
                        data-testid={`reject-btn-${r.id}`}
                        className="border-red-300 text-red-600 hover:bg-red-50 h-7 px-2"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Two columns: My / All */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* My Requests */}
          <Card className="bg-white border-[#E2E8F0] shadow-sm">
            <CardHeader className="pb-2 pt-3">
              <CardTitle className="text-sm font-bold text-[#052E16] font-['Manrope'] flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-[#16A34A]" />
                Moje žádosti <span className="text-[#94A3B8] font-medium">({myRequests.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 pb-3">
              {myRequests.length === 0 ? (
                <p className="text-sm text-[#94A3B8] py-3 text-center">Nemáte žádné žádosti</p>
              ) : (
                <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
                  {myRequests.map(r => <RequestItem key={r.id} r={r} />)}
                </div>
              )}
            </CardContent>
          </Card>

          {/* All Requests */}
          <Card className="bg-white border-[#E2E8F0] shadow-sm">
            <CardHeader className="pb-2 pt-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle className="text-sm font-bold text-[#052E16] font-['Manrope'] flex items-center gap-2">
                  Všechny žádosti <span className="text-[#94A3B8] font-medium">({filteredAll.length})</span>
                </CardTitle>
                <div className="flex gap-1">
                  {[
                    { key: 'pending', label: 'Čeká' },
                    { key: 'approved', label: 'Schválené' },
                    { key: 'rejected', label: 'Zamítnuté' },
                    { key: 'all', label: 'Vše' },
                  ].map(f => (
                    <button
                      key={f.key}
                      onClick={() => setAllFilter(f.key)}
                      data-testid={`filter-${f.key}`}
                      className={`px-2 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wide transition-colors ${
                        allFilter === f.key
                          ? 'bg-[#052E16] text-white'
                          : 'bg-[#F1F5F9] text-[#57534E] hover:bg-[#E2E8F0]'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0 pb-3">
              {filteredAll.length === 0 ? (
                <p className="text-sm text-[#94A3B8] py-3 text-center">Žádné žádosti</p>
              ) : (
                <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
                  {filteredAll.slice(0, 100).map(r => <RequestItem key={r.id} r={r} showUser />)}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Calendar */}
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardHeader className="pb-2 pt-3">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={prevMonth} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <CardTitle className="text-base font-bold text-[#052E16] font-['Manrope']">
                {MONTHS_CZ[calMonth]} {calYear}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={nextMonth} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0 pb-3">
            <div className="grid grid-cols-7 gap-px">
              {DAYS_CZ.map(d => (
                <div key={d} className="text-center text-[10px] font-bold text-[#94A3B8] uppercase py-1.5">{d}</div>
              ))}
              {days.map((day, idx) => {
                const dateStr = day ? toIsoDate(calYear, calMonth, day) : '';
                const isToday = dateStr === todayStr;
                const dayReqs = day ? monthRequests.filter(r => isInRange(calYear, calMonth, day, r.date_from, r.date_to)) : [];
                return (
                  <div
                    key={idx}
                    className={`min-h-[68px] p-1 border border-[#F1F5F9] rounded-md ${
                      !day ? 'bg-transparent' : isToday ? 'bg-[#F0FDF4] border-[#16A34A]' : 'bg-white'
                    }`}
                    data-testid={day ? `cal-day-${dateStr}` : undefined}
                  >
                    {day && (
                      <>
                        <span className={`text-xs font-medium ${isToday ? 'text-[#16A34A] font-bold' : 'text-[#57534E]'}`}>{day}</span>
                        <div className="space-y-0.5 mt-0.5">
                          {dayReqs.slice(0, 3).map(r => (
                            <div
                              key={r.id}
                              className="text-[9px] leading-tight px-1 py-0.5 rounded truncate font-medium"
                              style={{
                                backgroundColor: r.leave_type_color + '20',
                                color: r.leave_type_color,
                                borderLeft: `2px solid ${r.leave_type_color}`,
                                opacity: r.status === 'pending' ? 0.65 : 1
                              }}
                              title={`${r.user_name}: ${r.leave_type_name} (${r.status === 'pending' ? 'čeká' : 'schváleno'})`}
                            >
                              {r.user_name?.split(' ')[0]}
                            </div>
                          ))}
                          {dayReqs.length > 3 && <div className="text-[9px] text-[#94A3B8]">+{dayReqs.length - 3}</div>}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Legend */}
            <div className="flex items-center gap-3 mt-2 text-[10px] text-[#94A3B8]">
              <span>Legenda:</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> Schváleno</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500 opacity-65" /> Čeká</span>
              <span className="text-[#94A3B8]">Zamítnuté se v kalendáři nezobrazují.</span>
            </div>
          </CardContent>
        </Card>

        {/* Reject Dialog */}
        <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) { setRejectTarget(null); setRejectReason(''); } }}>
          <DialogContent className="sm:max-w-[425px]" data-testid="reject-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Manrope'] text-[#DC2626]">Zamítnutí žádosti</DialogTitle>
            </DialogHeader>
            {rejectTarget && (
              <div className="space-y-3 py-2">
                <p className="text-sm text-[#57534E]">
                  Zamítáte žádost <strong className="text-[#1C1917]">{rejectTarget.user_name}</strong>
                  &nbsp;({fmtCz(rejectTarget.date_from)} — {fmtCz(rejectTarget.date_to)}).
                </p>
                <div>
                  <Label className="text-[#57534E]">Důvod zamítnutí</Label>
                  <textarea
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    rows={3}
                    placeholder="Volitelný důvod, který uvidí zaměstnanec..."
                    data-testid="reject-reason-input"
                    className="mt-1 w-full rounded-md border border-[#E2E8F0] bg-white px-3 py-2 text-sm text-[#1C1917] focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setRejectTarget(null); setRejectReason(''); }}>Zrušit</Button>
              <Button
                onClick={confirmReject}
                disabled={decidingId === rejectTarget?.id}
                data-testid="confirm-reject-btn"
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {decidingId === rejectTarget?.id ? 'Zamítám...' : 'Potvrdit zamítnutí'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* New Request Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-[425px]" data-testid="new-vacation-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Manrope'] text-[#052E16]">Nová žádost o dovolenou</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-[#57534E]">Typ dovolené *</Label>
                <select
                  value={formType}
                  onChange={e => setFormType(e.target.value)}
                  data-testid="vacation-type-select"
                  className="mt-1 w-full rounded-md border border-[#E2E8F0] bg-white px-3 py-2 text-sm text-[#1C1917] focus:border-[#16A34A] focus:outline-none focus:ring-1 focus:ring-[#16A34A]"
                >
                  <option value="">Vyberte typ</option>
                  {leaveTypes.map(lt => (
                    <option key={lt.id} value={lt.id}>{lt.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#57534E]">Od *</Label>
                  <Input type="date" value={formFrom} onChange={e => setFormFrom(e.target.value)} data-testid="vacation-from" className="mt-1" />
                </div>
                <div>
                  <Label className="text-[#57534E]">Do *</Label>
                  <Input type="date" value={formTo} onChange={e => setFormTo(e.target.value)} data-testid="vacation-to" className="mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-[#57534E]">Poznámka</Label>
                <Input value={formNote} onChange={e => setFormNote(e.target.value)} placeholder="Volitelná poznámka..." data-testid="vacation-note" className="mt-1" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Zrušit</Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="vacation-submit" className="bg-[#16A34A] hover:bg-[#15803D] text-white">
                {submitting ? 'Odesílám...' : 'Odeslat žádost'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
